import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Info, DollarSign, TrendingUp, Calendar, FileSpreadsheet, Lightbulb, Calculator, PiggyBank, AlertTriangle } from 'lucide-react';

export function BusinesscaseGuideModal() {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8">
          <Info className="h-5 w-5" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] p-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <DialogTitle className="text-2xl font-bold">
            Businesscase Handleiding
          </DialogTitle>
          <p className="text-muted-foreground">
            Complete uitleg over het invullen en interpreteren van de businesscase
          </p>
        </DialogHeader>
        
        <ScrollArea className="h-[70vh] px-6 py-4">
          <div className="space-y-8">
            
            {/* Section 1: Wat is een Businesscase */}
            <section className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Calculator className="h-5 w-5 text-primary" />
                </div>
                <h2 className="text-xl font-bold">1. Wat is een Businesscase?</h2>
              </div>
              
              <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                <p>
                  Een businesscase berekent de <strong>financiële haalbaarheid</strong> van een batterijsysteem. 
                  Het vergelijkt de investering met de verwachte opbrengsten over een periode van typisch 10-15 jaar.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                  <div className="bg-green-50 dark:bg-green-950 p-3 rounded-lg border border-green-200 dark:border-green-800">
                    <p className="font-semibold text-green-800 dark:text-green-200">✅ Positief als:</p>
                    <ul className="text-sm text-green-700 dark:text-green-300 mt-1 space-y-1">
                      <li>• ROI jaar 1 &gt; 10%</li>
                      <li>• Terugverdientijd &lt; 7 jaar</li>
                      <li>• Positieve cashflow</li>
                    </ul>
                  </div>
                  <div className="bg-yellow-50 dark:bg-yellow-950 p-3 rounded-lg border border-yellow-200 dark:border-yellow-800">
                    <p className="font-semibold text-yellow-800 dark:text-yellow-200">⚠️ Twijfelachtig als:</p>
                    <ul className="text-sm text-yellow-700 dark:text-yellow-300 mt-1 space-y-1">
                      <li>• ROI jaar 1 5-10%</li>
                      <li>• Terugverdientijd 7-10 jaar</li>
                      <li>• Krappe marges</li>
                    </ul>
                  </div>
                  <div className="bg-red-50 dark:bg-red-950 p-3 rounded-lg border border-red-200 dark:border-red-800">
                    <p className="font-semibold text-red-800 dark:text-red-200">❌ Negatief als:</p>
                    <ul className="text-sm text-red-700 dark:text-red-300 mt-1 space-y-1">
                      <li>• ROI jaar 1 &lt; 5%</li>
                      <li>• Terugverdientijd &gt; 10 jaar</li>
                      <li>• Negatieve cashflow</li>
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            {/* Section 2: Kosten Invoeren (CAPEX) */}
            <section className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <DollarSign className="h-5 w-5 text-primary" />
                </div>
                <h2 className="text-xl font-bold">2. Kosten Invoeren (CAPEX)</h2>
              </div>
              
              <p className="text-muted-foreground">
                CAPEX = Capital Expenditure. Dit zijn alle eenmalige kosten die je maakt voor het batterijsysteem.
              </p>
              
              <div className="overflow-x-auto">
                <table className="w-full text-sm border rounded-lg overflow-hidden">
                  <thead className="bg-muted">
                    <tr>
                      <th className="text-left p-3 font-semibold">Veld</th>
                      <th className="text-left p-3 font-semibold">Wat is het?</th>
                      <th className="text-left p-3 font-semibold">Typische waarde</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    <tr>
                      <td className="p-3 font-medium">Batterij Aanschaf</td>
                      <td className="p-3">Aankoopprijs van het batterijsysteem. Bereken: €/kWh × capaciteit</td>
                      <td className="p-3 text-muted-foreground">€200.000 - €800.000</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-medium">Installatie Kosten</td>
                      <td className="p-3">Fysieke installatie: aansluiting, bekabeling, omvormer, arbeid</td>
                      <td className="p-3 text-muted-foreground">€20.000 - €100.000</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-medium">Software Setup</td>
                      <td className="p-3">Eenmalige configuratie EMS (Energy Management System)</td>
                      <td className="p-3 text-muted-foreground">€2.500 - €10.000</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-medium">Jaarlijks Abonnement</td>
                      <td className="p-3">Eerste jaar wordt meegeteld in CAPEX, daarna als OPEX (EMS Costs)</td>
                      <td className="p-3 text-muted-foreground">€3.000 - €15.000</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-medium">Overige Kosten</td>
                      <td className="p-3">Engineering, vergunningen, projectmanagement, onvoorzien</td>
                      <td className="p-3 text-muted-foreground">€5.000 - €30.000</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-medium">Extra Kosten</td>
                      <td className="p-3">Individueel te specificeren items met naam en bedrag</td>
                      <td className="p-3 text-muted-foreground">Variabel</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  <strong>💡 Tip:</strong> Vraag altijd een gedetailleerde offerte aan de leverancier. 
                  Alle kosten moeten inclusief BTW zijn als de klant geen BTW kan terugvorderen.
                </p>
              </div>
            </section>

            {/* Section 3: Subsidies */}
            <section className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <PiggyBank className="h-5 w-5 text-primary" />
                </div>
                <h2 className="text-xl font-bold">3. Subsidies Configureren</h2>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full text-sm border rounded-lg overflow-hidden">
                  <thead className="bg-muted">
                    <tr>
                      <th className="text-left p-3 font-semibold">Subsidie</th>
                      <th className="text-left p-3 font-semibold">Uitleg</th>
                      <th className="text-left p-3 font-semibold">Berekening</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    <tr>
                      <td className="p-3 font-medium">EIA (Energie Investerings Aftrek)</td>
                      <td className="p-3">Fiscale aftrek voor duurzame investeringen. De 40% aftrek verlaagt de belastbare winst.</td>
                      <td className="p-3 font-mono text-xs bg-muted/50 rounded">40% × investering × belastingtarief</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-medium">Flex-E</td>
                      <td className="p-3">Overheidssubsidie specifiek voor batterijopslag. Standaard 35% van investering, handmatig aanpasbaar.</td>
                      <td className="p-3 font-mono text-xs bg-muted/50 rounded">Vast bedrag (standaard 35%)</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-medium">Overige Subsidies</td>
                      <td className="p-3">SDE++, provinciale regelingen, gemeentelijke subsidies, etc.</td>
                      <td className="p-3 font-mono text-xs bg-muted/50 rounded">Direct af van investering</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg p-4">
                  <p className="font-semibold text-green-800 dark:text-green-200 mb-2">EIA Voorbeeld</p>
                  <div className="text-sm text-green-700 dark:text-green-300 space-y-1">
                    <p>Investering: €500.000</p>
                    <p>EIA aftrek: 40% = €200.000</p>
                    <p>Belastingtarief: 25%</p>
                    <p className="font-bold">Netto voordeel: €50.000</p>
                  </div>
                </div>
                <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <p className="font-semibold text-blue-800 dark:text-blue-200 mb-2">Flex-E Voorbeeld</p>
                  <div className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
                    <p>Investering: €500.000</p>
                    <p>Flex-E percentage: 35%</p>
                    <p className="font-bold">Subsidie: €175.000</p>
                    <p className="text-xs mt-2">Let op: Flex-E heeft maximum bedragen</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Section 4: Jaarlijkse Opbrengsten */}
            <section className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <TrendingUp className="h-5 w-5 text-primary" />
                </div>
                <h2 className="text-xl font-bold">4. Jaarlijkse Opbrengsten Invullen</h2>
              </div>
              
              <p className="text-muted-foreground">
                Vul voor elk jaar de verwachte opbrengsten en kosten in. Gebruik data van Zympler of vul handmatig in.
              </p>
              
              <div className="overflow-x-auto">
                <table className="w-full text-sm border rounded-lg overflow-hidden">
                  <thead className="bg-muted">
                    <tr>
                      <th className="text-left p-3 font-semibold">Kolom</th>
                      <th className="text-left p-3 font-semibold">Wat is het?</th>
                      <th className="text-left p-3 font-semibold">Typische waarde/jaar</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    <tr className="bg-green-50/50 dark:bg-green-950/30">
                      <td className="p-3 font-medium text-green-700 dark:text-green-300">Grid Connection</td>
                      <td className="p-3">Besparing op aansluitkosten door peak shaving</td>
                      <td className="p-3 text-muted-foreground">€0 - €50.000</td>
                    </tr>
                    <tr className="bg-green-50/50 dark:bg-green-950/30">
                      <td className="p-3 font-medium text-green-700 dark:text-green-300">Grid Purchase</td>
                      <td className="p-3">Besparing op inkoop energie door slim laden/ontladen</td>
                      <td className="p-3 text-muted-foreground">€5.000 - €30.000</td>
                    </tr>
                    <tr className="bg-green-50/50 dark:bg-green-950/30">
                      <td className="p-3 font-medium text-green-700 dark:text-green-300">Dayahead</td>
                      <td className="p-3">Handelen op de dayahead elektriciteitsmarkt (EPEX)</td>
                      <td className="p-3 text-muted-foreground">€10.000 - €80.000</td>
                    </tr>
                    <tr className="bg-green-50/50 dark:bg-green-950/30">
                      <td className="p-3 font-medium text-green-700 dark:text-green-300">HBE's</td>
                      <td className="p-3">Hernieuwbare Brandstofeenheden certificaten</td>
                      <td className="p-3 text-muted-foreground">€1.000 - €10.000</td>
                    </tr>
                    <tr className="bg-green-50/50 dark:bg-green-950/30">
                      <td className="p-3 font-medium text-green-700 dark:text-green-300">Energy Tax</td>
                      <td className="p-3">Besparing energiebelasting door eigen gebruik</td>
                      <td className="p-3 text-muted-foreground">€2.000 - €15.000</td>
                    </tr>
                    <tr className="bg-green-50/50 dark:bg-green-950/30">
                      <td className="p-3 font-medium text-green-700 dark:text-green-300">Endex</td>
                      <td className="p-3">Hedging voordelen door slimme contracten</td>
                      <td className="p-3 text-muted-foreground">€0 - €20.000</td>
                    </tr>
                    <tr className="bg-green-50/50 dark:bg-green-950/30">
                      <td className="p-3 font-medium text-green-700 dark:text-green-300">Imbalance Income</td>
                      <td className="p-3">Opbrengsten uit onbalansmarkt diensten</td>
                      <td className="p-3 text-muted-foreground">€5.000 - €40.000</td>
                    </tr>
                    <tr className="bg-green-50/50 dark:bg-green-950/30">
                      <td className="p-3 font-medium text-green-700 dark:text-green-300">Imbalance Discount</td>
                      <td className="p-3">Korting op onbalansdiensten vergoeding</td>
                      <td className="p-3 text-muted-foreground">€0 - €5.000</td>
                    </tr>
                    <tr className="bg-red-50/50 dark:bg-red-950/30">
                      <td className="p-3 font-medium text-red-700 dark:text-red-300">EMS Costs (-)</td>
                      <td className="p-3">Jaarlijkse softwarekosten voor energiemanagement</td>
                      <td className="p-3 text-muted-foreground">-€3.000 tot -€15.000</td>
                    </tr>
                    <tr className="bg-red-50/50 dark:bg-red-950/30">
                      <td className="p-3 font-medium text-red-700 dark:text-red-300">Total OPEX (-)</td>
                      <td className="p-3">Overige operationele kosten (onderhoud, verzekering)</td>
                      <td className="p-3 text-muted-foreground">-€1.000 tot -€10.000</td>
                    </tr>
                    <tr className="bg-green-50/50 dark:bg-green-950/30">
                      <td className="p-3 font-medium text-green-700 dark:text-green-300">Generator</td>
                      <td className="p-3">Besparing op diesel/noodstroom door batterij backup</td>
                      <td className="p-3 text-muted-foreground">€0 - €20.000</td>
                    </tr>
                    <tr className="bg-green-50/50 dark:bg-green-950/30">
                      <td className="p-3 font-medium text-green-700 dark:text-green-300">Extra SDE</td>
                      <td className="p-3">Additionele SDE++ opbrengsten via batterij</td>
                      <td className="p-3 text-muted-foreground">€0 - €30.000</td>
                    </tr>
                    <tr className="bg-green-50/50 dark:bg-green-950/30">
                      <td className="p-3 font-medium text-green-700 dark:text-green-300">Total Solar</td>
                      <td className="p-3">Extra zonne-energie opbrengsten door batterijopslag</td>
                      <td className="p-3 text-muted-foreground">€0 - €50.000</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              <div className="bg-yellow-50 dark:bg-yellow-950 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-semibold text-yellow-800 dark:text-yellow-200">Let op: Kosten zijn negatief</p>
                    <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                      EMS Costs en Total OPEX worden als <strong>negatieve</strong> getallen ingevoerd of automatisch 
                      als kosten behandeld. Dit wordt afgetrokken van de omzet.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Section 5: ROI Berekening */}
            <section className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <TrendingUp className="h-5 w-5 text-primary" />
                </div>
                <h2 className="text-xl font-bold">5. ROI Berekening Uitgelegd</h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-muted/50 rounded-lg p-4 border">
                  <h3 className="font-semibold mb-2">Bruto ROI</h3>
                  <div className="font-mono text-sm bg-background p-3 rounded border mb-3">
                    (Omzet Jaar 1 / Netto Investering) × 100%
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Kijkt alleen naar de bruto omzet zonder operationele kosten. 
                    Geeft een optimistisch beeld.
                  </p>
                </div>
                <div className="bg-muted/50 rounded-lg p-4 border">
                  <h3 className="font-semibold mb-2">Netto ROI</h3>
                  <div className="font-mono text-sm bg-background p-3 rounded border mb-3">
                    ((Omzet - OPEX) Jaar 1 / Netto Investering) × 100%
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Houdt rekening met operationele kosten. 
                    Geeft een realistischer beeld.
                  </p>
                </div>
              </div>
              
              <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                <h3 className="font-semibold">Scenario's</h3>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div className="p-3 bg-green-100 dark:bg-green-900 rounded-lg">
                    <p className="text-xs text-green-700 dark:text-green-300">Best Case</p>
                    <p className="font-bold text-green-800 dark:text-green-200">+15%</p>
                    <p className="text-xs text-green-600 dark:text-green-400">Optimistisch scenario</p>
                  </div>
                  <div className="p-3 bg-blue-100 dark:bg-blue-900 rounded-lg">
                    <p className="text-xs text-blue-700 dark:text-blue-300">Base Case</p>
                    <p className="font-bold text-blue-800 dark:text-blue-200">Berekend</p>
                    <p className="text-xs text-blue-600 dark:text-blue-400">Meest waarschijnlijk</p>
                  </div>
                  <div className="p-3 bg-orange-100 dark:bg-orange-900 rounded-lg">
                    <p className="text-xs text-orange-700 dark:text-orange-300">Worst Case</p>
                    <p className="font-bold text-orange-800 dark:text-orange-200">-10%</p>
                    <p className="text-xs text-orange-600 dark:text-orange-400">Conservatief scenario</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Section 6: Terugverdientijd */}
            <section className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Calendar className="h-5 w-5 text-primary" />
                </div>
                <h2 className="text-xl font-bold">6. Terugverdientijd Interpreteren</h2>
              </div>
              
              <div className="bg-muted/50 rounded-lg p-4">
                <p className="mb-4">
                  De terugverdientijd is het eerste jaar waarin de cumulatieve cashflow positief wordt. 
                  Dit betekent dat je op dat moment je initiële investering hebt terugverdiend.
                </p>
                
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div className="p-3 bg-green-100 dark:bg-green-900 rounded-lg">
                    <p className="text-2xl font-bold text-green-800 dark:text-green-200">&lt; 5 jaar</p>
                    <p className="text-sm text-green-700 dark:text-green-300">Uitstekend</p>
                  </div>
                  <div className="p-3 bg-yellow-100 dark:bg-yellow-900 rounded-lg">
                    <p className="text-2xl font-bold text-yellow-800 dark:text-yellow-200">5-7 jaar</p>
                    <p className="text-sm text-yellow-700 dark:text-yellow-300">Acceptabel</p>
                  </div>
                  <div className="p-3 bg-red-100 dark:bg-red-900 rounded-lg">
                    <p className="text-2xl font-bold text-red-800 dark:text-red-200">&gt; 7 jaar</p>
                    <p className="text-sm text-red-700 dark:text-red-300">Twijfelachtig</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  <strong>💡 Tip:</strong> Batterijen hebben typisch een levensduur van 10-15 jaar. 
                  Een terugverdientijd van meer dan 7-8 jaar is risicovol omdat je dan weinig 
                  profitabele jaren overhoudt.
                </p>
              </div>
            </section>

            {/* Section 7: Data Importeren */}
            <section className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <FileSpreadsheet className="h-5 w-5 text-primary" />
                </div>
                <h2 className="text-xl font-bold">7. Data Importeren</h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-muted/50 rounded-lg p-4 border">
                  <h3 className="font-semibold mb-2">📊 Zympler Import</h3>
                  <p className="text-sm text-muted-foreground">
                    Exporteer data uit Zympler en importeer direct. 
                    Kolommen worden automatisch gematched.
                  </p>
                </div>
                <div className="bg-muted/50 rounded-lg p-4 border">
                  <h3 className="font-semibold mb-2">📥 Excel Import</h3>
                  <p className="text-sm text-muted-foreground">
                    Upload een Excel bestand met jaarlijkse data. 
                    Download eerst de template voor het juiste format.
                  </p>
                </div>
                <div className="bg-muted/50 rounded-lg p-4 border">
                  <h3 className="font-semibold mb-2">✏️ Handmatig</h3>
                  <p className="text-sm text-muted-foreground">
                    Vul de tabel cel voor cel in. 
                    Ideaal voor kleine aanpassingen of snelle schattingen.
                  </p>
                </div>
              </div>
            </section>

            {/* Section 8: Tips & Best Practices */}
            <section className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Lightbulb className="h-5 w-5 text-primary" />
                </div>
                <h2 className="text-xl font-bold">8. Tips & Best Practices</h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div className="flex items-start gap-3 p-3 bg-green-50 dark:bg-green-950 rounded-lg">
                    <span className="text-lg">✅</span>
                    <div>
                      <p className="font-medium text-green-800 dark:text-green-200">Altijd 10+ jaar berekenen</p>
                      <p className="text-sm text-green-700 dark:text-green-300">
                        Bekijk het volledige financiële plaatje over de levensduur van de batterij.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-green-50 dark:bg-green-950 rounded-lg">
                    <span className="text-lg">✅</span>
                    <div>
                      <p className="font-medium text-green-800 dark:text-green-200">Conservatief inschatten</p>
                      <p className="text-sm text-green-700 dark:text-green-300">
                        Liever positief verrast worden dan negatief. Gebruik worst case voor beslissingen.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-green-50 dark:bg-green-950 rounded-lg">
                    <span className="text-lg">✅</span>
                    <div>
                      <p className="font-medium text-green-800 dark:text-green-200">Vergelijk scenario's</p>
                      <p className="text-sm text-green-700 dark:text-green-300">
                        Maak meerdere businesscases voor verschillende batterijgroottes.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-start gap-3 p-3 bg-red-50 dark:bg-red-950 rounded-lg">
                    <span className="text-lg">❌</span>
                    <div>
                      <p className="font-medium text-red-800 dark:text-red-200">Vermijd te optimistische prijzen</p>
                      <p className="text-sm text-red-700 dark:text-red-300">
                        Energieprijzen fluctueren. Gebruik conservatieve marktprijzen.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-red-50 dark:bg-red-950 rounded-lg">
                    <span className="text-lg">❌</span>
                    <div>
                      <p className="font-medium text-red-800 dark:text-red-200">Vergeet geen kosten</p>
                      <p className="text-sm text-red-700 dark:text-red-300">
                        Onderhoud, verzekering en vervanging van componenten worden vaak vergeten.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-red-50 dark:bg-red-950 rounded-lg">
                    <span className="text-lg">❌</span>
                    <div>
                      <p className="font-medium text-red-800 dark:text-red-200">Reken niet op onzekere subsidies</p>
                      <p className="text-sm text-red-700 dark:text-red-300">
                        Neem alleen subsidies mee waarvan toekenning zeker is.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 mt-4">
                <p className="font-semibold text-primary mb-2">🎯 Golden Rule</p>
                <p className="text-sm">
                  Een goede businesscase heeft een <strong>ROI jaar 1 &gt; 12%</strong>, een <strong>terugverdientijd &lt; 6 jaar</strong>, 
                  en een <strong>positieve cashflow over de hele looptijd</strong>. Die derde eis is de beslissende: 
                  een hoge ROI in jaar 1 zegt niets over de looptijd, want de opbrengst daalt en de OPEX stijgt.
                </p>
              </div>
            </section>
            
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
